'use client';

import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { translations } from '@/i18n/translations';
import { FadeInView } from '@/components/animations/FadeInView';

interface Feature {
  id: string;
  icon: string;
  index: number;
}

interface FeaturesSectionProps {
  className?: string;
}

const FEATURES: Feature[] = [
  { id: 'smart-list',       icon: '📋', index: 0 },
  { id: 'batch-detail',     icon: '🔍', index: 1 },
  { id: 'batch-image',      icon: '🖼️', index: 2 },
  { id: 'email-extraction', icon: '📧', index: 3 },
  { id: 'data-management',  icon: '💾', index: 4 },
  { id: 'i18n-support',     icon: '🌐', index: 5 },
];

export function FeaturesSection({ className = '' }: FeaturesSectionProps) {
  const { t, language } = useLanguage();
  const [activeId, setActiveId] = useState('smart-list');

  const activeFeature = FEATURES.find((f) => f.id === activeId) ?? FEATURES[0];
  const i = activeFeature.index;

  return (
    <section className={`py-20 px-4 md:px-8 lg:px-16 bg-white ${className}`}>
      <div className="max-w-7xl mx-auto">
        <FadeInView>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('features.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              强大的功能，让数据提取变得简单高效
            </p>
          </div>
        </FadeInView>

        {/* 桌面端：左右分栏 */}
        <FadeInView>
          <div className="hidden md:flex gap-6 lg:gap-10 items-start">
            {/* 左侧功能列表 */}
            <div className="w-64 lg:w-72 flex-shrink-0 flex flex-col gap-1">
              {FEATURES.map((feature) => {
                const isActive = feature.id === activeId;
                const n = feature.index;
                return (
                  <button
                    key={feature.id}
                    onMouseEnter={() => setActiveId(feature.id)}
                    onClick={() => setActiveId(feature.id)}
                    className={`
                      relative text-left px-4 py-3.5 rounded-xl transition-all duration-200
                      ${isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-3 bottom-3 w-0.5 bg-primary-500 rounded-full" />
                    )}
                    <div className="flex items-center gap-3 pl-2">
                      <span className="text-2xl">{feature.icon}</span>
                      <div className="min-w-0">
                        <div className={`font-semibold text-sm leading-tight ${isActive ? 'text-primary-700' : 'text-gray-800'}`}>
                          {t(`features.items.${n}.title`)}
                        </div>
                        <div className={`text-xs mt-0.5 truncate ${isActive ? 'text-primary-500' : 'text-gray-400'}`}>
                          {t(`features.items.${n}.tagline`)}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 右侧详情展示 */}
            <div className="flex-1 min-h-72">
              <div
                key={activeFeature.id}
                className="bg-gray-50 rounded-2xl p-8 lg:p-10 h-full animate-fade-in"
              >
                <div className="text-5xl mb-5">{activeFeature.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t(`features.items.${i}.title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed text-base mb-6">
                  {t(`features.items.${i}.description`)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {translations[language].features.items[i].keywords.map((kw: string) => (
                    <span
                      key={kw}
                      className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full font-medium"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </FadeInView>

        {/* 移动端：横向 Tab + 内容区 */}
        <FadeInView>
          <div className="md:hidden">
            <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide">
              {FEATURES.map((feature) => {
                const isActive = feature.id === activeId;
                return (
                  <button
                    key={feature.id}
                    onClick={() => setActiveId(feature.id)}
                    className={`
                      flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200
                      ${isActive
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600'
                      }
                    `}
                  >
                    <span>{feature.icon}</span>
                    <span>{t(`features.items.${feature.index}.title`)}</span>
                  </button>
                );
              })}
            </div>

            <div
              key={activeFeature.id + '-mobile'}
              className="mt-4 bg-gray-50 rounded-2xl p-6 animate-fade-in"
            >
              <p className="text-gray-600 leading-relaxed text-sm mb-4">
                {t(`features.items.${i}.description`)}
              </p>
              <div className="flex flex-wrap gap-2">
                {translations[language].features.items[i].keywords.map((kw: string) => (
                  <span
                    key={kw}
                    className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </FadeInView>
      </div>
    </section>
  );
}
